import {
  Anchor,
  Badge,
  Button,
  createTheme,
  Drawer,
  Input,
  Modal,
  Table,
  Title,
  type MantineColorsTuple,
} from '@mantine/core';
import { DateInput, DatePickerInput } from '@mantine/dates';

import { todayIso } from '@/shared/format';

/**
 * Single governed design system (D10; docs/design/design-system.md). Brand customisation per
 * tenant (§57.16) is limited to theme tokens, so everything visual has to be reachable from here.
 *
 * Index 7 of every ramp is the design's reference hue (`primaryShade`), so `color="green"`
 * fills with the same green the mockups use; 0–2 are the tints behind badges and selections.
 */

/** Steel: the FMC Sistemas palette, the product's primary. 7 = acero profundo (#1D2D3D). */
const steel: MantineColorsTuple = [
  '#F1F5F9',
  '#E6EDF4',
  '#C9D6E3',
  '#A9BDD0',
  '#7F98B0',
  '#5980A6',
  '#416180',
  '#1D2D3D',
  '#17242F',
  '#111B24',
];

/** Neutrals derived from the ink (#1D1F20): text, borders, surfaces. */
const gray: MantineColorsTuple = [
  '#F7F8F9',
  '#F2F2F3',
  '#E7E9EC',
  '#D9DCE0',
  '#B4B9BE',
  // `AM-197`: was #8A9096 (2.9:1 on the paper): tertiary text at WCAG AA.
  '#6A6F74',
  '#5C6166',
  '#3F4448',
  '#2A2E31',
  '#1D1F20',
];

/** Éxito: resuelto, SLA cumplido, facturable. */
const green: MantineColorsTuple = [
  '#E6F2EC',
  '#CFE6DA',
  '#A9D3BE',
  '#82BFA2',
  '#5FAC89',
  '#45996F',
  '#3A8C66',
  '#2E7D5B',
  '#266A4D',
  '#1F573F',
];

/** Advertencia: en espera, SLA en riesgo, nota interna. */
const yellow: MantineColorsTuple = [
  '#FBF3E4',
  '#F5E4C2',
  '#ECCF95',
  '#E2B968',
  '#D8A445',
  '#CB922F',
  '#C08527',
  '#B7791F',
  '#9A6519',
  '#7D5214',
];

/** Peligro: prioridad crítica, SLA incumplido, acciones destructivas. */
const red: MantineColorsTuple = [
  '#F8E9E9',
  '#F1D2D2',
  '#E3A9A9',
  '#D68080',
  '#C95C5C',
  '#BE4747',
  '#B84040',
  '#B23A3A',
  '#963030',
  '#7A2727',
];

/** Prioridad alta. */
const orange: MantineColorsTuple = [
  '#FAEDE5',
  '#F4D8C7',
  '#EAB79A',
  '#DF956D',
  '#D57B4B',
  '#CC6E39',
  '#C76A32',
  '#C2652B',
  '#A45523',
  '#86451C',
];

/** Hex used by the PWA manifest and the `theme-color` meta tag (acero profundo). */
export const BRAND_COLOR = '#1D2D3D';

/** Hanken Grotesk, self-hosted through @fontsource-variable (never fetched from a third party). */
export const FONT_STACK = "'Hanken Grotesk Variable', 'Segoe UI', system-ui, sans-serif";

/** Headings and ticket numbers: weight 500 with closed tracking, per the design system. */
export const HEADING_LETTER_SPACING = '-0.02em';

/**
 * A day of the date pickers (`yyyy-MM-dd`) is marked `data-today` by the browser's clock, and the
 * "today" of the console is the tenant's (`AM-282`): it turns over at the midnight of the tenant's
 * zone, not the browser's. The props of a day come last into the cell, so this one wins; `null`
 * and not `undefined` takes the mark off the day the browser thinks is today, because Mantine
 * drops an `undefined` prop before it merges them (its type for the data attributes does not
 * allow `null`, React does: the attribute is not rendered).
 */
export function tenantTodayDayProps(date: string): { 'data-today': boolean | undefined } {
  return { 'data-today': (date === todayIso() ? true : null) as boolean | undefined };
}

export const theme = createTheme({
  primaryColor: 'steel',
  primaryShade: 7,
  colors: { steel, gray, green, yellow, red, orange },
  black: '#1D1F20',
  white: '#FFFFFF',
  fontFamily: FONT_STACK,
  fontFamilyMonospace: FONT_STACK,
  // Body 14/20; table text 13/18; metadata 12/16 (design system, "Tipografía").
  fontSizes: { xs: '12px', sm: '13px', md: '14px', lg: '16px', xl: '18px' },
  lineHeights: { xs: '1.34', sm: '1.4', md: '1.43', lg: '1.5', xl: '1.45' },
  headings: {
    fontFamily: FONT_STACK,
    fontWeight: '500',
    sizes: {
      h1: { fontSize: '32px', lineHeight: '1.125' },
      h2: { fontSize: '26px', lineHeight: '1.15' },
      h3: { fontSize: '22px', lineHeight: '1.18' },
      h4: { fontSize: '18px', lineHeight: '1.22' },
      h5: { fontSize: '16px', lineHeight: '1.25' },
      h6: { fontSize: '14px', lineHeight: '1.43' },
    },
  },
  radius: { xs: '2px', sm: '4px', md: '6px', lg: '8px', xl: '12px' },
  defaultRadius: 'sm',
  // Dense data screens (§3.5): the default Mantine spacing is too generous for tables.
  spacing: { xs: '0.5rem', sm: '0.75rem', md: '1rem', lg: '1.5rem', xl: '2rem' },
  cursorType: 'pointer',
  components: {
    Title: Title.extend({
      styles: { root: { letterSpacing: HEADING_LETTER_SPACING } },
    }),
    Anchor: Anchor.extend({
      defaultProps: { c: 'steel.6', underline: 'hover' },
    }),
    // Controls are 32 px high in the console and administration (size "sm"), 40 px in the
    // portal and on phones (size "md").
    Button: Button.extend({
      defaultProps: { size: 'sm' },
      // Ronda 4 (puntos 3 y 6): el botón de Mantine es `overflow: hidden`, y por eso tiene un ancho
      // mínimo intrínseco casi nulo: en una fila `nowrap` se achica hasta cortarse ("Qui", "Vincu",
      // "Det"). Igual que la insignia, nunca cede espacio: que ceda el texto vecino, que se parte en
      // renglones, o que la fila pase a la línea siguiente.
      styles: { root: { flexShrink: 0, minWidth: 'max-content' } },
      vars: (_theme, props) => {
        if (props.size === 'sm' || props.size === undefined) {
          return {
            root: {
              '--button-height': '32px',
              '--button-padding-x': '12px',
              '--button-fz': '13px',
            },
          };
        }
        if (props.size === 'md') {
          return {
            root: {
              '--button-height': '40px',
              '--button-padding-x': '16px',
              '--button-fz': '14px',
            },
          };
        }
        return { root: {} };
      },
    }),
    // A dialog wider than the phone pushes its own buttons off the screen, which is how a
    // "Guardar" ends up unclickable (13 §5.1). Capping the width at the viewport costs nothing on
    // a desktop - where the size already decides - and keeps the phone's dialog inside the screen.
    Modal: Modal.extend({
      defaultProps: { size: 'md' },
      styles: {
        content: { maxWidth: '100vw' },
      },
    }),
    Drawer: Drawer.extend({
      styles: {
        content: { maxWidth: '100vw' },
      },
    }),
    Input: Input.extend({
      defaultProps: { size: 'sm' },
      vars: (_theme, props) => {
        if (props.size === 'sm' || props.size === undefined) {
          return {
            wrapper: {
              '--input-height': '32px',
              '--input-fz': '13px',
              '--input-padding-x': '10px',
            },
          };
        }
        if (props.size === 'md') {
          return {
            wrapper: {
              '--input-height': '40px',
              '--input-fz': '14px',
              '--input-padding-x': '12px',
            },
          };
        }
        return { wrapper: {} };
      },
    }),
    Table: Table.extend({
      defaultProps: {
        verticalSpacing: 6,
        horizontalSpacing: 'sm',
        fz: 'sm',
        highlightOnHover: true,
      },
    }),
    // The pickers mark today (a thin border, `highlightToday`) and it is the tenant's today
    // (`tenantTodayDayProps`). The default lives on each picker the console uses and not on the
    // `Month` they draw, because the pickers compose the day props of the page with their own.
    // The pages use `TenantDateInput`, which renders this same `DateInput`, so the default holds.
    DateInput: DateInput.extend({
      defaultProps: { highlightToday: true, getDayProps: tenantTodayDayProps },
    }),
    DatePickerInput: DatePickerInput.extend({
      defaultProps: { highlightToday: true, getDayProps: tenantTodayDayProps },
    }),
    // Revisión del manual, puntos 7 y 8, y ronda 4 (punto 2): la etiqueta de una insignia es
    // `overflow: hidden` por diseño (para el texto largo que sí necesita elipsis), y eso le da a la
    // insignia un ancho mínimo intrínseco casi nulo: cualquier celda de tabla de ancho automático o
    // fila flexible la achica por debajo de su texto y termina cortada ("APROB…", "PENDIE…") aunque
    // el contenedor pudiera desbordar. `flexShrink: 0` la saca del cálculo de las filas flexibles y
    // `minWidth: max-content` de las tablas: nunca cede espacio, así que es el contenedor (la fila,
    // la tabla) el que desborda y se puede desplazar, no el texto el que se recorta.
    Badge: Badge.extend({
      styles: { root: { flexShrink: 0, minWidth: 'max-content' } },
    }),
  },
});
